import { Messages } from '~src/codegen/Localize';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { ISiteTrial, TrialStatus } from '~src/siteManagement/serverApi/types/ISiteTrial';
import { IGroupBasicInfo } from '~src/userManagement/groups/models/IGroupInfo';

import { Agent, AgentPerm } from './AgentSectionTypes';

export const getTestId = (component: string, key?: string) => `settings_agents_${component}${key ? `_${key}` : ''}`;

// Whether an agent's TYPE statically supports group-based access: it declares a group setting
// (tableauKnowledge opts out with null) and its group flags allow groups. Runtime state (enabled,
// available, the try-it trial gate) is layered on by callers, not here.
export const agentSupportsGroups = (agent: Agent): boolean =>
    agent.groupIdSiteSetting != null
    && (!agent.readyForGroupsFlag || !!FeatureFlags[agent.readyForGroupsFlag])
    && !(agent.dashboardQAFlag && FeatureFlags[agent.dashboardQAFlag]);

// Composes an agent's rendered label: "<displayName>: <description>" via the locale-aware
// messageWithColonSeparator. Every agent carries both getters (see Agent), so the composition
// is unconditional. The '*' is a runtime trial-site marker (not translatable), shown on trial
// sites (isTrialSite) for registration-gated agents in the trial (requiresRegistration && availableInTrial).
export const getAgentLabel = (agent: Agent, isTrialSite: boolean): string => {
    const showTrialMarker = isTrialSite && agent.requiresRegistration && agent.availableInTrial;
    const label = Messages.messageWithColonSeparator({ MESSAGE_PART_1: agent.displayName(), MESSAGE_PART_2: agent.description() });
    return showTrialMarker ? `*${label}` : label;
};

// Maps each perm to its optional per-perm namespace override field on Agent.
const NAMESPACE_FIELD_FOR_PERM: Record<AgentPerm, 'enabledNamespace' | 'groupIdNamespace'> = {
    [AgentPerm.Enabled]: 'enabledNamespace',
    [AgentPerm.GroupId]: 'groupIdNamespace',
};

// Resolves the Formik field path for an agent's enabled/groupId setting. A per-perm namespace
// override (enabledNamespace/groupIdNamespace) wins; otherwise the shared settingsNamespace
// applies; otherwise the key is top-level under `settings.*`.
export const getAgentFieldPath = (agent: Agent, perm: AgentPerm): string => {
    const namespace = agent[NAMESPACE_FIELD_FOR_PERM[perm]] ?? agent.settingsNamespace;
    return namespace ? `settings.${namespace}.${agent[perm]}` : `settings.${agent[perm]}`;
};

export const _groupName = (groups: IGroupBasicInfo[]) => (id: string) => {
    const group = groups.find(g => g.groupLuid === id);
    return group?.name ?? group?.groupLuid ?? Messages.settings_agents_invalid_group();
};

export type AgentAvailabilityInfo = {
    actionButtonDisabled: boolean;
    isAvailable: boolean;
}

// Whether an agent's feature is available under the Tableau AI trial in try-it mode.
//
// Matching is driven by `restartFeatures`, NOT `onboardedFeatures`: per the getTrials contract
// (see W-21711350 restart-trial design) `onboardedFeatures` holds raw YAML DB keys (e.g.
// `generativeaiPulseDiscover`) that do not match `agent.enabledSiteSetting`, while `restartFeatures`
// holds serialized names that do. `restartFeatures` is defined as `registry − onboarded`, and the
// client's copy of "feature is in the trial registry" is the static `availableInTableauAITrial`
// flag — so `onboarded = registry − restartFeatures` is derivable without touching the raw
// `onboardedFeatures` list at all.
//
// - Pre-trial (no trial object): fall back to the static registry flag.
// - Not in the trial registry: never available.
// - In the registry and the trial is restartable: available (it is either onboarded now or would
//   be onboarded on restart — both eligible).
// - In the registry and not restartable: available only if onboarded, i.e. NOT in restartFeatures.
export const isAgentInTableauAITrial = (agent: Agent, tableauAITrial?: ISiteTrial): boolean => {
    if (!tableauAITrial) {
        return agent.availableInTableauAITrial;
    }
    if (!agent.availableInTableauAITrial) {
        return false;
    }
    if (tableauAITrial.canRestart) {
        return true;
    }
    const restartFeatures = tableauAITrial.restartFeatures ?? [];
    return restartFeatures.indexOf(agent.enabledSiteSetting) === -1;
};

export const getAgentAvailabilityInfo = ({
    agent,
    allowTestRegistration,
    isTrialSite,
    isTryItMode,
    registered,
    tableauAITrial,
}: {
    agent: Agent;
    allowTestRegistration: boolean;
    isTrialSite: boolean;
    isTryItMode: boolean;
    registered: boolean;
    tableauAITrial?: ISiteTrial;
}): AgentAvailabilityInfo => {
    // In try-it mode the Tableau AI trial decides availability, so registration grants access only
    // outside it: a registered-but-not-Tableau+ site is in try-it mode and must be driven by the
    // trial like any non-registered try-it site. See W-23816522.
    const registrationGrantsAccess = registered && !isTryItMode;
    const noRegistrationNeeded = !agent.requiresRegistration;
    const coveredByTrialSite = isTrialSite && agent.availableInTrial;
    // Paths that grant access with the toggle enabled — as opposed to the try-it trial path, which
    // grants availability but disables the toggle when the trial is missing/expired.
    const grantsFullAccess = registrationGrantsAccess || noRegistrationNeeded || coveredByTrialSite;

    const availableViaTrial = isTryItMode && isAgentInTableauAITrial(agent, tableauAITrial);
    // Test hook: with the registration FF (tabGAITokenServiceRegistration) OFF, skip the
    // Salesforce-registration requirement so dev/test can use the UI without that backend.
    const availableViaTestHook = !isTryItMode && allowTestRegistration;

    if (!(grantsFullAccess || availableViaTrial || availableViaTestHook)) {
        return { isAvailable: false, actionButtonDisabled: true };
    }

    const trialInactive = !tableauAITrial || tableauAITrial.status === TrialStatus.EXPIRED;
    const actionButtonDisabled = availableViaTrial && !grantsFullAccess && trialInactive;
    return { isAvailable: true, actionButtonDisabled };
};
